// components/artifacts/renderers/ArtifactContentRenderer.tsx
import React from "react";
import { ArtifactItem, ArtifactLoadingPlaceholder } from "@/types/Artifact";
import { StreamingArtifactRenderer } from "./StreamingArtifactRenderer";
import { CodeArtifactRenderer } from "./CodeArtifactRenderer";
import { GraphArtifactRenderer } from "./GraphArtifactRenderer";
import { TableArtifactRenderer } from "./TableArtifactRenderer";
import { SqlArtifactRenderer } from "./SqlArtifactRenderer";
import { MapArtifactRenderer } from "./MapArtifactRenderer";
import { LinkArtifactRenderer } from "./LinkArtifactRenderer";

interface ArtifactContentRendererProps {
  item: ArtifactItem | ArtifactLoadingPlaceholder;
}

export const ArtifactContentRenderer: React.FC<ArtifactContentRendererProps> = React.memo(({ 
  item 
}) => {
  // Handle loading state
  if ('isLoading' in item && item.isLoading) {
    return <StreamingArtifactRenderer loadingItem={item} />;
  }

  // Handle completed artifacts
  const artifact = item as ArtifactItem;
  
  switch (artifact.type) {
    case "code":
      return <CodeArtifactRenderer artifact={artifact as any} />;
      
    case "graph":
      return <GraphArtifactRenderer artifact={artifact as any} />;
      
    case "table":
      return <TableArtifactRenderer artifact={artifact as any} />;
      
    case "sql":
      return <SqlArtifactRenderer artifact={artifact as any} />;
      
    case "map":
      return <MapArtifactRenderer artifact={artifact as any} />;
      
    case "link":
      return <LinkArtifactRenderer artifact={artifact as any} />;
      
    case "visualization":
      // Placeholder for future visualization types
      return (
        <div className="p-4">
          <pre className="whitespace-pre-wrap">
            {typeof artifact.content === "string"
              ? artifact.content
              : JSON.stringify(artifact.content, null, 2)}
          </pre>
        </div>
      );
      
    default:
      // Fallback for unknown types
      return (
        <div className="p-4">
          <p className="mb-2 text-sm text-muted-foreground">
            Unknown artifact type: {(artifact as any).type}
          </p>
          <pre className="whitespace-pre-wrap text-sm">
            {typeof (artifact as any).content === "string"
              ? (artifact as any).content
              : JSON.stringify((artifact as any).content, null, 2)}
          </pre>
        </div>
      );
  }
});